import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PALIA_CATEGORIES } from "../../config/paliaCategories";
import { isAdminUser } from "../../lib/auth";
import { supabase } from "../../lib/supabaseClient";
import TermDiscussion from "../community/TermDiscussion";
import TermDiscussionEdit from "../community/TermDiscussionEdit";
import "./DetailedView.css";

const isValidHttpUrl = (value) => {
    if (!value) return true;
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const normalizeTagsText = (tagsText) =>
    (tagsText || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .join(", ");

const parseCategories = (value, max = 2) =>
    (value || "")
        .split(/[,/|&]+/g)
        .map((c) => c.trim())
        .filter(Boolean)
        .slice(0, max);

const formatCategories = (cat1, cat2) => {
    const out = [];
    if (cat1) out.push(cat1.trim());
    if (cat2 && cat2.trim() && cat2.trim() !== cat1.trim())
        out.push(cat2.trim());
    return out.join(", ");
};

export default function DetailedView() {
    const { id } = useParams();
    const navigate = useNavigate();

    // view state
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // auth/admin state
    const [session, setSession] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminCheckLoading, setAdminCheckLoading] = useState(true);

    // edit state
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    const categories = useMemo(() => {
        return Array.isArray(PALIA_CATEGORIES) && PALIA_CATEGORIES.length
            ? PALIA_CATEGORIES
            : ["Other"];
    }, []);

    // ---------- Auth session ----------
    useEffect(() => {
        supabase.auth.getSession().then(({ data }) => {
            setSession(data.session || null);
        });

        const { data: sub } = supabase.auth.onAuthStateChange(
            (_event, newSession) => {
                setSession(newSession);
            },
        );

        return () => sub.subscription.unsubscribe();
    }, []);

    // ---------- Admin check ----------
    useEffect(() => {
        const run = async () => {
            setAdminCheckLoading(true);
            setIsAdmin(false);

            if (!session?.user) {
                setAdminCheckLoading(false);
                return;
            }

            const ok = await isAdminUser();
            setIsAdmin(ok);

            setAdminCheckLoading(false);
        };

        run();
    }, [session]);

    // ---------- Load term ----------
    const load = async () => {
        setLoading(true);
        setMsg("");

        const { data, error } = await supabase
            .from("term_submissions")
            .select(
                "id, term, definition, category, tags, palia_pedia_u, created_at, submitted_by, approved",
            )
            .eq("id", id)
            .eq("approved", true)
            .single();

        setLoading(false);

        if (error) {
            setMsg(error.message);
            setItem(null);
            return;
        }

        setItem(data);

        if (!editing) {
            const [cat1, cat2] = parseCategories(data.category, 2);
            setDraft({
                term: data.term || "",
                definition: data.definition || "",
                category1: cat1 || categories[0] || "Other",
                category2: cat2 || "",
                tags: data.tags || "",
                palia_pedia_u: data.palia_pedia_u || "",
            });
        }
    };

    useEffect(() => {
        if (id) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // ---------- Edit helpers ----------
    const startEdit = () => {
        if (!item) return;
        setMsg("");
        setEditing(true);

        const [cat1, cat2] = parseCategories(item.category, 2);

        setDraft({
            term: item.term || "",
            definition: item.definition || "",
            category1: cat1 || categories[0] || "Other",
            category2: cat2 || "",
            tags: item.tags || "",
            palia_pedia_u: item.palia_pedia_u || "",
        });
    };

    const cancelEdit = () => {
        setEditing(false);
        setMsg("");

        if (!item) return;

        const [cat1, cat2] = parseCategories(item.category, 2);

        setDraft({
            term: item.term || "",
            definition: item.definition || "",
            category1: cat1 || categories[0] || "Other",
            category2: cat2 || "",
            tags: item.tags || "",
            palia_pedia_u: item.palia_pedia_u || "",
        });
    };

    const onDraftChange = (e) => {
        const { name, value } = e.target;
        setDraft((prev) => ({ ...prev, [name]: value }));
    };

    const validateDraft = (d) => {
        const errs = [];
        if (!d.term.trim()) errs.push("Term is required.");
        if (!d.definition.trim()) errs.push("Definition is required.");

        if (!d.category1 || !categories.includes(d.category1)) {
            errs.push("Category is required (pick from list).");
        }

        if (d.category2 && !categories.includes(d.category2)) {
            errs.push("Second category must be from the list.");
        }

        if (d.category2 && d.category1 && d.category2 === d.category1) {
            errs.push("Second category must be different.");
        }

        if (
            d.palia_pedia_u?.trim() &&
            !isValidHttpUrl(d.palia_pedia_u.trim())
        ) {
            errs.push("PaliaPedia link must be a valid http(s) URL.");
        }

        return errs;
    };

    const save = async () => {
        if (!isAdmin) return;
        if (!item?.id || !draft) return;

        setMsg("");

        const errs = validateDraft(draft);
        if (errs.length) {
            setMsg(errs.join(" "));
            return;
        }

        setSaving(true);

        const payload = {
            term: draft.term.trim(),
            definition: draft.definition.trim(),
            category: formatCategories(draft.category1, draft.category2),
            tags: normalizeTagsText(draft.tags),
            palia_pedia_u: draft.palia_pedia_u.trim() || null,
        };

        const { error } = await supabase
            .from("term_submissions")
            .update(payload)
            .eq("id", item.id);

        setSaving(false);

        if (error) {
            setMsg(error.message);
            return;
        }

        setEditing(false);
        await load();
        setMsg("Saved.");
    };

    const tags =
        item?.tags
            ?.split(",")
            .map((t) => t.trim())
            .filter(Boolean) || [];

    const viewCategories = parseCategories(item?.category, 2);

    return (
        <div className="details-container">
            <div className="details-card">
                <div className="details-top">
                    <button
                        className="details-back"
                        onClick={() => navigate("/")}
                    >
                        ← Back
                    </button>

                    {/* Admin actions */}
                    {!adminCheckLoading && isAdmin && !loading && item && (
                        <div className="details-admin-actions">
                            {!editing ? (
                                <button
                                    className="details-admin-btn"
                                    onClick={startEdit}
                                >
                                    Edit
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="details-admin-btn details-admin-secondary"
                                        onClick={cancelEdit}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="details-admin-btn"
                                        onClick={save}
                                        disabled={saving}
                                    >
                                        {saving ? "Saving..." : "Save"}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {loading ? <p className="details-subtext">Loading…</p> : null}
                {!loading && msg ? (
                    <div className="details-message">{msg}</div>
                ) : null}
                {!loading && !item && !msg ? (
                    <p className="details-subtext">Not found.</p>
                ) : null}

                {!loading && item ? (
                    <>
                        <div className="details-title-row">
                            {!editing ? (
                                <>
                                    <h2 className="details-title">
                                        {item.term}
                                    </h2>

                                    {/* show up to 2 category pills */}
                                    <div className="term-category-pills">
                                        {(viewCategories.length
                                            ? viewCategories
                                            : ["Uncategorized"]
                                        ).map((c) => (
                                            <span key={c} className="pill">
                                                {c}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="details-edit-grid">
                                    <div className="details-field">
                                        <label>Term</label>
                                        <input
                                            name="term"
                                            value={draft?.term ?? ""}
                                            onChange={onDraftChange}
                                        />
                                    </div>

                                    <div className="details-field">
                                        <label>Category</label>
                                        <select
                                            name="category1"
                                            value={
                                                draft?.category1 ??
                                                categories[0] ??
                                                "Other"
                                            }
                                            onChange={onDraftChange}
                                        >
                                            {categories.map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="details-field">
                                        <label>
                                            Second Category (optional)
                                        </label>
                                        <select
                                            name="category2"
                                            value={draft?.category2 ?? ""}
                                            onChange={onDraftChange}
                                        >
                                            <option value="">— None —</option>
                                            {categories
                                                .filter(
                                                    (c) =>
                                                        c !==
                                                        (draft?.category1 ??
                                                            ""),
                                                )
                                                .map((c) => (
                                                    <option key={c} value={c}>
                                                        {c}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        {!editing ? (
                            <p className="details-definition">
                                {item.definition}
                            </p>
                        ) : (
                            <div className="details-field">
                                <label>Definition</label>
                                <textarea
                                    name="definition"
                                    rows={5}
                                    value={draft?.definition ?? ""}
                                    onChange={onDraftChange}
                                />
                            </div>
                        )}

                        {!editing ? (
                            <>
                                {tags.length > 0 && (
                                    <div className="details-tags">
                                        {tags.map((t) => (
                                            <span
                                                key={t}
                                                className="pill pill-muted"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="details-field">
                                <label>Tags (comma-separated)</label>
                                <input
                                    name="tags"
                                    value={draft?.tags ?? ""}
                                    onChange={onDraftChange}
                                    placeholder="movement, early-game, bug catching"
                                />
                            </div>
                        )}

                        {!editing ? (
                            item.palia_pedia_u ? (
                                <div className="details-link">
                                    <a
                                        href={item.palia_pedia_u}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View on PaliaPedia
                                    </a>
                                </div>
                            ) : null
                        ) : (
                            <div className="details-field">
                                <label>PaliaPedia Link (optional)</label>
                                <input
                                    name="palia_pedia_u"
                                    value={draft?.palia_pedia_u ?? ""}
                                    onChange={onDraftChange}
                                    placeholder="https://paliapedia.com/..."
                                />
                            </div>
                        )}

                        <div className="details-divider" />

                        <div className="details-section">
                            {!editing ? (
                                <TermDiscussion termId={item.id} />
                            ) : isAdmin ? (
                                <TermDiscussionEdit termId={item.id} />
                            ) : (
                                <TermDiscussion termId={item.id} />
                            )}
                        </div>

                        <div className="details-section">
                            <h3>Related Guides</h3>
                            <p className="details-subtext">
                                Coming soon: farming routes, videos, tips, and
                                strategies.
                            </p>
                            <div className="details-empty">
                                No related guides yet.
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
